import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Form,
  Header,
  SpaceBetween,
  Tabs,
  Icon,
  Grid,
  GridProps,
  TextContent,
} from '@amzn/awsui-components-core';
import {
  Attachment,
  Autotag,
  CodeLink,
  CommentConversion,
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentNode,
  ContentNodeType,
  ContentType,
  ContentWorkflow,
  ContentWorkingCopy,
  ReviewDecision,
  ReviewRequestDecision,
  UserType,
  WorkflowState,
  WorkingCopyKey,
  UserCommentState,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import {
  findNodeById,
  getNodeHref,
  guideOrStrategy,
  resetOpenSourceCodeReview,
  supportsCodeRepos,
} from '../../../api/ContentUtil';
import * as UtilApi from '../../../api/UtilsApi';
import constants from '../../../common/constants';
import { ContentWorkingCopyManager } from '../../../common/ContentWorkingCopyManager';
import FlashbarMessageBroker from '../../../common/FlashbarMessageBroker';
import {
  createEditContentPageInstructions,
  editContentPageHeaders,
  finalizeContentPageHeaders,
  metadataToAutotagTypeMap,
} from '../../../components/utils/CreateEditContentUtils';
import { Location } from 'history';
import { localLog } from '../../../loggers/Logger';
import { ContentSchemaSection, ContentSectionKey } from '../../../model/ContentSchemaSection';
import { AttachmentList } from '../../common/AttachmentList';
import { AuthorsList } from '../../common/AuthorsList';
import InfoLink from '../../common/InfoLink';
import LoadingPage from '../../common/LoadingPage';
import NoticeOfPublication from '../../common/NoticeOfPublication';
import ContentDataForm from '../../content/ContentDataForm';
import { ContentAudienceOptions, ContentTypeOptions } from '../../utils/SelectableOptions';
import AuthorControls from '../action-controls/AuthorControls';
import { ContentViewerContext } from '../ContentViewerContext';
import PublicationHistory from '../PublicationHistory';
import { AddNewContentPageButton } from './AddNewContentPageButton';
import ContentChecklist from './ContentChecklist';
import ContentDataControls from './ContentDataControls';
import { ContentPageNavigation } from './ContentPageNavigation';
import ContentTitle from './ContentTitle';
import ContentVersion from './ContentVersion';
import EditContentControls from './EditContentControls';
import LastPublicationDate from './LastPublicationDate';
import MetadataDisplay from './MetadataDisplay';
import MetadataTileInput from './MetadataTileInput';
import { ReorderContentPageButton } from './ReorderContentPageButton';
import SubmitForReviewControl from './SubmitForReviewControl';
import CodeRepoList from './CodeRepoList';
import MetadataSelectInput from './MetadataSelectInput';
import ContentConverter from './ContentConverter';
import {
  noReviewerAlert,
  shouldResetContentChecklistDueToAudienceChange,
  shouldResetContentChecklistDueToContentTypeChange,
  shouldResetContentChecklistDueToHasCodeChange,
} from './ContentUtils';
import { ContentDataProps } from './ContentData';
import {
  isCodeInvalid,
  validateCodeRepoAudience,
  validateCodeReview,
  validateContent,
  validateContentForCodeBlocks,
} from '../../../api/ValidationUtil';
import {
  isAuthorOrCoAuthor,
  isContentValid,
  isReviewersValid,
  isInPeerReview,
  isAllowedToSeeAppSecLink,
} from '../../utils/ContentDataUtils';
import BaseContentData, { BaseContentDataState } from './BaseContentData';
import ContentMetadataForm from '../../content/ContentMetadataForm';
import TutorialLink from '../../content/TutorialLink';
import { missingRequiredSections } from '../../utils/ContentDataUtils';
import ContentQualityEvalucationCard from '../../content-quality/ContentQualityEvaluationCard';
import { InlineCommentList } from '../../inline-comment';
import { WorkingCopyStatusType } from '../../common/WorkingCopyStatus';
import MetadataEditorInput from './MetadataEditorInput';
import TechwriterMetadataEditor from './TechwriterMetadataEditor';
import { contentAudienceToRepoTypeMap } from '../../../api/UtilsApi';
import InputSizeMessage from '../../utils/InputSizeMessage';
import { ROOT_SECTION_ID } from '../../../model/Contents';

/**
 * State for EditContentData, extending the shared BaseContentDataState.
 *
 * This state is large because the edit view has to track everything about the in-progress
 * edit session — the working copy, validation errors, checklist progress, UI toggles, and
 * the state machines for content-type conversion.
 *
 * Grouped conceptually:
 *
 * - Working copy & dirty tracking:
 *     updatedContentAudience, isContentUnchanged, isContentNewOrModified,
 *     workingCopyStatus, isLoadingWorkingCopy
 *
 * - Validation (three categories of errors, combined when deciding "can save?"):
 *     formError (shown in summary banner)
 *     metadataValidationError / contentDataValidationError (per-field errors)
 *     codeRepoValidationError[3] (code blocks / sec review / repo audience)
 *
 * - Reviewers/checklist tab state:
 *     reviewers, isEditReviewersEnabled, isDeleteReviewersEnabled,
 *     isContentChecklistComplete, isContentAuthorCoAuthor, shouldResetChecklist
 *
 * - Content-type conversion wizard state (Pattern ↔ Guide ↔ Strategy):
 *     showContentConverter, fromContentType, toContentType,
 *     commentConversions (comment re-mappings), contentConverted
 *
 * - UI toggles:
 *     showControls (hidden when another user holds the lock)
 *     infoExpanded (configuration section expanded/collapsed)
 *     isLoadingContentTemplate (blocks render until template schema loads)
 *     systemAddedTags (autotags added by ML, shown to tech writers)
 */
export interface EditContentDataState extends BaseContentDataState {
  updatedContentAudience: ContentAudience;
  showControls: boolean;
  formError: React.ReactElement | null;
  metadataValidationError?: any;
  contentDataValidationError?: any;
  infoExpanded: boolean;
  isContentUnchanged: boolean;
  reviewers?: ContentAccessUser[];
  isEditReviewersEnabled: boolean;
  isDeleteReviewersEnabled: boolean;
  isContentNewOrModified: boolean;
  workingCopyStatus: WorkingCopyStatusType;
  isLoadingWorkingCopy: boolean;
  showContentConverter?: boolean;
  fromContentType?: ContentType;
  toContentType?: ContentType;
  commentConversions?: CommentConversion[];
  contentConverted: boolean;
  isContentChecklistComplete: boolean;
  isContentAuthorCoAuthor: boolean;
  isLoadingContentTemplate: boolean;
  systemAddedTags?: Autotag[];
  shouldResetChecklist?: boolean;
  codeRepoValidationError: string[]; // [codeBlocksError, secReviewError, codeAudienceError]
}

// Positional indices into codeRepoValidationError — 3 independent checks stored as a tuple.
const CODE_BLOCK_VALIDATION_INDEX = 0;
const CODE_REVIEW_VALIDATION_INDEX = 1;
const CODE_REPO_AUDIENCE_VALIDATION_INDEX = 2;

/**
 * EDIT content view — the full content editor page.
 *
 * Renders the whole edit UI:
 *   - Page header + instructions
 *   - Tabs: Details / Content / Code / Attachments / Authors / Reviewers / History
 *   - Working-copy auto-save (every 3 seconds after change)
 *   - Validation alerts (title, code, metadata)
 *   - Save / Discard / Submit-for-Review controls
 *   - Content checklist + quality evaluation card (right column)
 *   - Content-type conversion wizard (Pattern ↔ Guide ↔ Strategy)
 *
 * This class is abstract-by-convention — the THREE action hooks are no-ops and MUST be
 * overridden by subclasses (e.g. DraftEditContentData):
 *   - saveChanges(unlock)               persist edits to backend
 *   - discardChanges()                  throw away edits, unlock workflow
 *   - exitEditModeAndSubmitForReview()  save & submit for peer review
 *
 * Subclasses also set `workingCopyKey` to pick which localStorage bucket to use
 * (different edit contexts — draft edit vs finalization — don't share working copies).
 */
class EditContentData extends BaseContentData<EditContentDataState> {
  private static defaultProps = {
    workflow: new ContentWorkflow(),
    workflowEvents: [],
    workflowHistory: [],
  };

  // Timer handle for the 3-second auto-save debounce. When set, a save is queued.
  protected savingLocalWorkingCopy: any;
  // Key identifying which localStorage/IndexedDB bucket to use for working copies.
  // Set by subclasses so draft-edit and finalization contexts don't collide.
  // @ts-expect-error TS2564 - strict migration
  protected workingCopyKey: WorkingCopyKey;
  // Service that persists/retrieves working copies to/from localStorage + IndexedDB.
  protected workingCopyMngr: ContentWorkingCopyManager;
  // Edit view — editing is always enabled. Used by BaseContentData utilities.
  protected isEditingEnabled: boolean = true;

  // Global flashbar for push notifications (e.g. "Working copy restored", validation errors).
  private flashbarMessageBroker: FlashbarMessageBroker;

  constructor(props: ContentDataProps) {
    super(props);
    this.workingCopyMngr = new ContentWorkingCopyManager();
    this.flashbarMessageBroker = FlashbarMessageBroker.create();

    const { coAuthors, contentOwner, workflow, content, showTechwriterMetadata, isAdminView } =
      props;
    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = props.appContext.authnInfo;
    const {
      // @ts-expect-error TS2339 - strict migration
      draftMetadataTags,
      // @ts-expect-error TS2339 - strict migration
      draftState,
      // @ts-expect-error TS2339 - strict migration
      lastReviewDecision,
      // @ts-expect-error TS2339 - strict migration
      lastReviewRequestDecision,
      // @ts-expect-error TS2339 - strict migration
      publicationMetadataTags,
    } = workflow;

    // Clone content so we can mutate a working copy without affecting props
    const updatedContent = this.cloneContent();
    const updatedContentAudience = this.props.contentAudience;
    const metadataValidationError = {
      contentMetadata: {},
      contentTypeMetadata: {},
      templateMetadata: {},
    };

    // Replace epic Id that use title as Id with uuid (legacy data migration)
    this.updateEpicIds(updatedContent);
    this.historyUnlisten = this.listenBrowserHistory();
    this.handleValidationError(metadataValidationError);

    // Determines whether current user is author/co-author — affects checklist visibility
    // and which controls are shown (authors see checklist; admins/techwriters skip it).
    const isContentAuthorCoAuthor = isAuthorOrCoAuthor({
      userProfile,
      coAuthors,
      contentOwner,
      workflow,
    });
    // Checklist is considered "complete" by default if the current user isn't the author
    // (admins/reviewers don't need to walk through the checklist) or is in admin view.
    const isContentChecklistComplete = !isContentAuthorCoAuthor || !!isAdminView;

    // "Edit reviewers" is gated: only enabled when checklist is done (so author thinks before
    // assigning), or in admin view, or during peer review (reviewers can swap themselves out).
    const isEditReviewersEnabled =
      (isContentChecklistComplete && !showTechwriterMetadata) ||
      !!isAdminView ||
      isInPeerReview(workflow);

    // "Delete reviewer" is more restrictive than edit: once a reviewer has DISAPPROVED,
    // author cannot remove them (must resubmit to same reviewer) unless the reviewer
    // declined the request or admin override.
    const isDeleteReviewersEnabled =
      lastReviewDecision !== ReviewDecision.DISAPPROVE ||
      lastReviewRequestDecision === ReviewRequestDecision.DECLINE ||
      !!isAdminView;

    // Tech-writer view uses publication metadata tags; draft edit view uses draft tags.
    const systemAddedTags = showTechwriterMetadata
      ? publicationMetadataTags?.systemAddedTags
      : draftMetadataTags?.systemAddedTags;
    this.state = {
      activeTabId: 'content',
      contentConverted: false,
      currentPage: updatedContent.contentData,
      expanded: true,
      fromContentType: updatedContent.contentMetadata.contentType,
      formError: null,
      infoExpanded: true,
      isContentChecklistComplete,
      // "New or Modified" is workflow state meaning content has been touched since last review.
      // Used in submit-for-review gating: can only submit if content is new/modified OR changed.
      isContentNewOrModified:
        draftState === WorkflowState.NEW || draftState === WorkflowState.MODIFIED,
      isContentUnchanged: true,
      isDeleteReviewersEnabled,
      isEditReviewersEnabled,
      metadataValidationError,
      // Hide Save/Discard controls when the workflow is locked by someone else.
      // (You can still see the content but editing is ineffective since server will reject.)
      showControls: !workflow || !workflow.lock || workflow.lock.acquiredBy === userProfile.userId,
      toContentType: updatedContent.contentMetadata.contentType,
      updatedContent,
      updatedContentAudience,
      location: { pathname: window?.location?.pathname } as Location,
      workingCopyStatus: WorkingCopyStatusType.Pending,
      isLoadingWorkingCopy: true,
      isLoadingContentTemplate: true,
      isContentAuthorCoAuthor,
      systemAddedTags,
      codeRepoValidationError: ['', '', ''],
    };
  }

  public async componentDidMount() {
    const hashTag = window?.location?.hash;
    if (hashTag) {
      // Delay hash navigation so React has rendered the target DOM node before we scroll to it.
      setTimeout(() => {
        this.updateCurrentPageByHashTag(hashTag, this.props.content);
      }, 500);
    } else {
      this.showContentSectionNavigation(this.props.content.contentData);
    }
    // Load reviewers + any auto-saved working copy in parallel-ish (awaited sequentially
    // because working-copy load may depend on reviewer data being in state).
    await this.loadReviewers();
    await this.loadWorkingCopy();
    // If this content is missing required sections (e.g. a legacy Pattern with fewer sections
    // than the current template requires), patch them in from the template.
    await this.addNewContentSections();
    // Run initial code-repo validations (three independent checks — see constants above).
    const { updatedContentAudience, updatedContent } = this.state;
    const codeRepoAudienceError = validateCodeRepoAudience(updatedContentAudience, updatedContent);
    const codeReviewCompleteError = validateCodeReview(updatedContentAudience, updatedContent);
    const codeBlocksValidationError = validateContentForCodeBlocks(updatedContent);
    this.setState({
      codeRepoValidationError: [
        codeBlocksValidationError,
        codeReviewCompleteError,
        codeRepoAudienceError,
      ],
    });
  }

  public componentDidUpdate(prevProps: ContentDataProps, prevState: EditContentDataState) {
    // Parent re-fetched content (after external update) → refresh working copy
    if (this.props.content !== prevProps.content) {
      this.handleContentUpdate();
    }

    // Audience changed externally (e.g. parent reset it) → sync our working copy
    if (this.props.contentAudience !== prevProps.contentAudience) {
      this.setState({ updatedContentAudience: this.props.contentAudience });
    }

    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;
    const {
      coAuthors,
      contentOwner,
      workflow,
      isEditingAllowed,
      showTechwriterMetadata,
      isAdminView,
    } = this.props;
    const { isContentChecklistComplete } = this.state;

    // Lock-ownership changed → show or hide Save/Discard controls accordingly
    if (isEditingAllowed !== prevProps.isEditingAllowed || workflow !== prevProps.workflow) {
      this.setState({
        // @ts-expect-error TS18048 - strict migration
        showControls: !workflow.lock || workflow.lock.acquiredBy === userProfile.userId,
      });
    }

    // Co-author list or workflow changed → re-evaluate whether current user is author/co-author
    // (affects many UI decisions like showing the content checklist).
    if (
      coAuthors !== prevProps.coAuthors ||
      contentOwner !== prevProps.contentOwner ||
      workflow !== prevProps.workflow
    ) {
      this.setState({
        isContentAuthorCoAuthor: isAuthorOrCoAuthor({
          userProfile,
          coAuthors,
          contentOwner,
          workflow,
        }),
      });
    }

    // Checklist completion flipped → re-compute edit-reviewers gating
    // (finishing the checklist unlocks reviewer editing for authors).
    if (isContentChecklistComplete !== prevState.isContentChecklistComplete) {
      const isEditReviewersEnabled =
        (isContentChecklistComplete && !showTechwriterMetadata) || !!isAdminView;
      this.setState({ isEditReviewersEnabled });
    }
  }

  public componentWillUnmount() {
    // Cancel pending auto-save to avoid unnecessary network calls / setState after unmount
    if (this.savingLocalWorkingCopy) {
      clearTimeout(this.savingLocalWorkingCopy);
    }
    this.historyUnlisten();
  }

  public render() {
    const {
      coAuthors,
      peerReviewers,
      contentOwner,
      updateInlineCommentsLocation,
      inlineComments,
      publicationState,
      reFetchInlineComments,
    } = this.props;
    const { updatedContent, isLoadingContentTemplate, isLoadingWorkingCopy, currentPage } =
      this.state;

    if (isLoadingWorkingCopy || isLoadingContentTemplate) {
      return <LoadingPage />;
    }

    const contentView = this.renderEditContentTabView();

    const ctx = {
      addNewPage: this.createNewPage,
      addNewSection: this.createNewSection,
      changeCurrentPage: this.pageChanged,
      content: updatedContent,
      currentPage,
      isEditingEnabled: this.isEditingEnabled,
      updateContent: this.updateContent,
      owner: contentOwner,
      coAuthors,
      peerReviewers,
      updateInlineCommentsLocation,
      inlineComments,
      publicationState,
      reFetchInlineComments,
    };

    return <ContentViewerContext.Provider value={ctx}>{contentView}</ContentViewerContext.Provider>;
  }

  // ─── ACTION HOOKS — MUST be overridden by subclasses ──────────────────────

  // Persists edits to the backend. If unlock=true, also releases the workflow edit lock
  // (triggered by "Save & Close"). Subclass implementation is expected to clean up the
  // working copy and notify the parent via onDisableEditMode. See DraftEditContentData.saveChanges.
  protected saveChanges = async (unlock: boolean) => {
    return null;
  };

  // Throws away all unsaved changes AND releases the workflow lock. Used by the
  // "Discard" button. Subclass implementation must also clean up the local working copy.
  protected discardChanges = async () => {
    return null;
  };

  // Discards edits, then submits the content for peer review (SEND_FOR_REVIEW event).
  // Also navigates to the appropriate dashboard afterwards (role-dependent).
  protected exitEditModeAndSubmitForReview = async () => {
    return null;
  };

  // Pre-save validation. Runs content + metadata validation; if anything fails, sets
  // formError with a bullet list of issues shown in an alert at the top of the form.
  // Returns true when the user can proceed to save, false when save should be blocked.
  // Also called before submit-for-review so the author can't submit invalid content.
  protected beforeSaving = async () => {
    const { updatedContent, metadataValidationError } = this.state;
    const entities = this.props.appContext?.entities || {};
    const {
      contentMetadataError,
      templateValidationError,
      contentDataLinkValidationError,
      isValid,
    } = validateContent(
      updatedContent,
      new Set([ContentSchemaSection.ContentMetadata, ContentSchemaSection.TemplateMetadata]),
      entities,
    );

    if (isValid) {
      this.setState({ formError: null });
      return true;
    } else {
      // Flatten both error maps into a single list of {key, value} pairs for display.
      // Deduplicate by key (Map drops duplicate keys — content errors override template errors).
      const errors = Array.from(new Map([...contentMetadataError, ...templateValidationError])).map(
        (e) => ({ key: e[0], value: e[1] }),
      );
      errors.push(...contentDataLinkValidationError);

      this.setState({
        formError: (
          <div>
            <SpaceBetween size="xxs">
              {errors.map((e) => (
                <Box key={e.key}>
                  <Box variant="strong" display="inline">
                    {e.key}:
                  </Box>{' '}
                  {e.value}
                </Box>
              ))}
            </SpaceBetween>
            {this.showPathSlug()}
          </div>
        ),
      });
      return false;
    }
  };

  // Hook for subclasses to append additional info to the validation error panel
  // (e.g. showing the path slug for URL-uniqueness errors). Default: nothing extra.
  protected showPathSlug = () => {
    return null;
  };

  private getContentDataControlProps = () => {
    const { submittingForReview, workflow } = this.props;
    const {
      updatedContent,
      isContentUnchanged,
      metadataValidationError,
      contentDataValidationError,
    } = this.state;
    const { contentMetadata } = updatedContent;
    const contentValid = isContentValid(metadataValidationError, contentDataValidationError);
    return {
      contentType: contentMetadata.contentType,
      disableButtons: submittingForReview,
      isEditingEnabled: this.isEditingEnabled,
      isSaveDisabled: !contentValid || isContentUnchanged,
      onBeforeSaving: this.beforeSaving,
      onDiscardChanges: this.discardChanges,
      onSaveChanges: this.saveChanges,
      workingCopyStatus: this.state.workingCopyStatus,
      isInPeerReview: isInPeerReview(workflow),
    };
  };

  private renderControls = () => {
    const { showContentControls } = this.props;
    const { showControls } = this.state;
    return showControls && showContentControls ? (
      <Box id="controls-container" padding={'s'}>
        <ContentDataControls {...this.getContentDataControlProps()} />
      </Box>
    ) : null;
  };

  private renderContentSection = () => {
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const gridDefinition: GridProps.ElementDefinition[] = [
      ContentType.Guide,
      ContentType.Strategy,
    ].includes(contentMetadata.contentType)
      ? [{ colspan: { m: 3, xxs: 12 } }, { colspan: { m: 9, xxs: 12 } }]
      : [{ colspan: { m: 12, xxs: 12 } }];
    return (
      <Grid gridDefinition={gridDefinition}>
        {this.renderPageNavigation()}
        {this.renderContentDataSections()}
      </Grid>
    );
  };

  private renderPageNavigation = () => {
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(contentType);
    const contentPageActions = (
      <div>
        <Box margin={{ top: 's' }}>
          <AddNewContentPageButton />
        </Box>
        <Box margin={{ top: 's' }}>
          <ReorderContentPageButton />
        </Box>
      </div>
    );
    return isGuideOrStrategy ? (
      <div>
        <ContentPageNavigation />
        {contentPageActions}
      </div>
    ) : null;
  };

  private renderContentDataSections = () => {
    const {
      coAuthors,
      contentId,
      workflow,
      workflowHistory,
      workflowId,
      showTechwriterMetadata,
      showSectionalComments,
      hideEmptySections,
    } = this.props;

    const { currentPage, updatedContent, expanded } = this.state;
    const { contentMetadata, contentTypeMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(contentType);

    return (
      <ContentDataForm
        expanded={expanded}
        contentId={contentId}
        workflowId={workflowId}
        workflow={workflow}
        workflowHistory={workflowHistory}
        coAuthors={coAuthors}
        templateSchema={UtilApi.getContentTemplateSchema(
          contentType,
          contentTypeMetadata,
          ContentSchemaSection.ContentSections,
        )}
        readonly={!this.isEditingEnabled}
        contentPage={currentPage}
        onContentChanged={this.handleContentDataChanged}
        onSectionsUpdated={this.handleSectionUpdated}
        onSectionDeleted={this.handleSectionDeleted}
        onPageTitleUpdated={this.handlePageTitleUpdated}
        onPageDeleted={this.handlePageDeleted}
        showTechwriterMetadata={showTechwriterMetadata}
        showPage={isGuideOrStrategy}
        hideEmptyContent={hideEmptySections}
        showSectionalComments={showSectionalComments}
        sectionsSortable={isGuideOrStrategy}
      />
    );
  };

  private pageChanged = async (selectedPage: ContentNode, depth: number) => {
    window.location.hash =
      selectedPage.id === ROOT_SECTION_ID ? '' : selectedPage.href || selectedPage.id;
    this.setState({
      currentPage: selectedPage,
    });
  };

  private createNewPage = async (newPage: ContentNode, parentPage?: ContentNode) => {
    const { updatedContent, currentPage } = this.state;

    if (parentPage) {
      const idx = parentPage.subContent.indexOf(currentPage);
      parentPage.subContent.splice(
        idx >= 0 ? idx + 1 : parentPage.subContent.length - 1,
        0,
        newPage,
      );
    } else {
      const idx = updatedContent.contentData.subContent.indexOf(currentPage);
      updatedContent.contentData.subContent.splice(
        idx >= 0 ? idx + 1 : updatedContent.contentData.subContent.length,
        0,
        newPage,
      );
    }
    this.updateCurrentPage(newPage);
  };

  private createNewSection = async () => {
    const { currentPage, updatedContent } = this.state;
    const { contentSections } = currentPage;
    const newSection = ContentNode.create('Untitled', '', ContentNodeType.Section, '');
    contentSections.push(newSection);
    this.updateCurrentPage(currentPage);
  };

  // Core content mutator. Called from every content-changing handler (text edits,
  // section updates, etc.). Marks content dirty and kicks off the auto-save debounce.
  private updateContent = async (updatedContent: Content) => {
    this.setState({
      isContentUnchanged: false,
      updatedContent,
    });
    this.saveLocalWorkingCopy();
  };

  private updateCurrentPage = (currentPage: ContentNode) => {
    this.setState({ currentPage, isContentUnchanged: false });
    this.saveLocalWorkingCopy();
  };

  private addOrReplaceCodeRepo = (codeRepo: CodeLink) => {
    const { updatedContent, updatedContentAudience, codeRepoValidationError } = this.state;
    const { contentTypeMetadata } = updatedContent;

    const currentContent = UtilApi.deepClone(updatedContent);

    if (codeRepo?.url) {
      contentTypeMetadata.codeLinks = [codeRepo];
    } else {
      contentTypeMetadata.codeLinks = [];
    }
    const shouldResetChecklist = shouldResetContentChecklistDueToHasCodeChange(
      currentContent,
      updatedContent,
    );
    const codeReviewCompleteError = validateCodeReview(updatedContentAudience, updatedContent);
    const codeRepoAudienceError = validateCodeRepoAudience(updatedContentAudience, updatedContent);
    codeRepoValidationError[CODE_REVIEW_VALIDATION_INDEX] = codeReviewCompleteError;
    codeRepoValidationError[CODE_REPO_AUDIENCE_VALIDATION_INDEX] = codeRepoAudienceError;
    this.setState({
      shouldResetChecklist,
      codeRepoValidationError,
    });
    this.updateContent(updatedContent);
  };

  private updateAttachments = (attachment: Attachment) => {
    const { updatedContent } = this.state;
    const { attachmentId } = attachment;
    // @ts-expect-error TS18048 - strict migration
    if (!updatedContent.attachments.includes(attachmentId)) {
      // @ts-expect-error TS18048 - strict migration
      updatedContent.attachments.push(attachmentId);
    }
    this.updateContent(updatedContent);
  };

  private removeAttachment = (attachmentId: string) => {
    const { updatedContent } = this.state;
    // @ts-expect-error TS18048 - strict migration
    const idx = updatedContent.attachments.findIndex((id) => id === attachmentId);
    if (idx >= 0) {
      // @ts-expect-error TS18048 - strict migration
      updatedContent.attachments.splice(idx, 1);
    }
    this.updateContent(updatedContent);
  };

  private toggleInfoExpanded = () => {
    const { infoExpanded } = this.state;
    this.setState({ infoExpanded: !infoExpanded });
  };

  private toggleExpanded = () => {
    const { expanded } = this.state;
    this.setState({ expanded: !expanded });
  };

  private handleContentMetadataInput = (
    sectionId: string,
    value: any,
    validationError?: string,
  ) => {
    const { updatedContent, metadataValidationError } = this.state;
    let updatedValue;
    switch (sectionId) {
      case 'tags':
        updatedValue = value
          .toLowerCase()
          .split(',')
          // @ts-expect-error TS7006 - strict migration
          .map((item) => item.trim())
          // @ts-expect-error TS7006 - strict migration
          .filter((item) => !!item);
        break;
      default:
        updatedValue = value;
        break;
    }

    // @ts-expect-error TS7053 - strict migration
    updatedContent.contentMetadata[sectionId] = updatedValue;
    // @ts-expect-error TS2322 - strict migration
    updatedContent.contentData.href = getNodeHref(
      updatedContent.contentData,
      updatedContent.contentMetadata,
    );
    metadataValidationError.contentMetadata[sectionId] = validationError;
    this.updateContent(updatedContent);
  };

  // AUDIENCE CHANGE handler (e.g. Internal → External).
  // Audience is a first-class concept: affects permissions, review requirements,
  // allowed code-repo types, and checklist validity. Changing it triggers:
  //   - Checklist reset check (Internal↔External boundary changes required review steps)
  //   - Code repo revalidation (External content needs extra security review)
  //   - Open-source code review reset (OSS review only applies to PUBLIC audience)
  private handleContentAudienceChanged = (
    sectionId: string,
    selectedId: string,
    validationError?: string,
  ) => {
    const {
      updatedContentAudience: contentAudience,
      codeRepoValidationError,
      updatedContent: content,
    } = this.state;

    // @ts-expect-error TS7053 - strict migration
    const updatedContentAudience = ContentAudience[selectedId];
    const shouldResetChecklist = shouldResetContentChecklistDueToAudienceChange(
      contentAudience,
      updatedContentAudience,
    );
    const codeReviewCompleteError = validateCodeReview(updatedContentAudience, content);
    const codeRepoAudienceError = validateCodeRepoAudience(updatedContentAudience, content);
    codeRepoValidationError[CODE_REVIEW_VALIDATION_INDEX] = codeReviewCompleteError;
    codeRepoValidationError[CODE_REPO_AUDIENCE_VALIDATION_INDEX] = codeRepoAudienceError;
    const updatedContent = resetOpenSourceCodeReview(contentAudience, content);
    this.setState({
      isContentUnchanged: false,
      updatedContent,
      updatedContentAudience,
      shouldResetChecklist,
      codeRepoValidationError,
    });
    this.handleValidationError(validationError);
    this.saveLocalWorkingCopy();
  };

  private handleTemplateMetadataInput = (
    sectionId: string,
    value: any,
    validationError?: string,
  ) => {
    const { updatedContent, metadataValidationError, systemAddedTags } = this.state;
    updatedContent.templateMetadata[sectionId] = value;
    metadataValidationError.templateMetadata[sectionId] = validationError;
    const autotagType = metadataToAutotagTypeMap.get(sectionId);
    if (autotagType && systemAddedTags) {
      const unmodifiedTags =
        systemAddedTags?.filter((systemAddedTag) => systemAddedTag.type !== autotagType) || [];
      const modifiedTag = systemAddedTags?.find(
        (systemAddedTag) => systemAddedTag.type === autotagType,
      );
      if (modifiedTag) {
        modifiedTag.tags = modifiedTag.tags?.filter((sectionTag) => value.includes(sectionTag));
        const updatedSystemAddedTags = [...unmodifiedTags, modifiedTag];
        this.setState({ systemAddedTags: updatedSystemAddedTags });
      }
    }
    this.updateContent(updatedContent);
    this.handleValidationError(metadataValidationError);
  };

  private handleSectionUpdated = async (contentSections: ContentNode[]) => {
    const { currentPage } = this.state;
    currentPage.contentSections = contentSections;
    this.updateCurrentPage(currentPage);
  };

  private handleContentDataChanged = async (
    sectionId: string,
    contentSectionKey: ContentSectionKey,
    newContent: any,
    validationError?: any,
  ) => {
    const { currentPage, updatedContent, codeRepoValidationError } = this.state;
    const { contentSections } = currentPage;
    const contentSectionIndex = contentSections.findIndex((section) => section.id === sectionId);

    if (contentSectionIndex >= 0) {
      // @ts-expect-error TS2532 - strict migration
      contentSections[contentSectionIndex][contentSectionKey] = newContent;
    } else {
      currentPage[contentSectionKey] = newContent;
    }

    const currentPageNode = findNodeById(currentPage.id, [updatedContent.contentData]);
    if (currentPageNode) {
      Object.assign(currentPageNode, { ...currentPage });
    }

    const codeBlocksValidationError = validateContentForCodeBlocks(updatedContent);
    codeRepoValidationError[CODE_BLOCK_VALIDATION_INDEX] = codeBlocksValidationError;

    // Combine setStates to reduce unnecessary re-render to improve front-end performance during editing
    this.setState({
      contentDataValidationError: validationError,
      codeRepoValidationError,
      currentPage,
      isContentUnchanged: false,
    });
    this.saveLocalWorkingCopy();
    this.handleValidationError(validationError);
  };

  private handleSectionDeleted = async (sectionId: string) => {
    const { currentPage } = this.state;
    currentPage.contentSections = currentPage.contentSections.filter(
      (section) => section.id !== sectionId,
    );
    this.setState({
      currentPage,
    });
    this.saveLocalWorkingCopy();
  };

  private handlePageTitleUpdated = async (pageId: string, pageTitle: any, shortTitle?: any) => {
    const { updatedContent, currentPage } = this.state;
    currentPage.title = pageTitle;
    currentPage.shortTitle = shortTitle;
    // @ts-expect-error TS2322 - strict migration
    currentPage.href = getNodeHref(currentPage, this.props.content?.contentMetadata);
    const pageNode = findNodeById(pageId, [updatedContent.contentData]);
    if (pageNode) {
      pageNode.title = pageTitle;
      pageNode.shortTitle = shortTitle;
      pageNode.href = currentPage.href;
    } else {
      this.flashbarMessageBroker.pushInfoMessage(constants.CONTENT_PAGE_INVALID_TEXT);
    }
    this.updateCurrentPage(currentPage);
    this.updateContent(updatedContent);
  };

  private handlePageDeleted = async (pageId: string) => {
    const { updatedContent } = this.state;

    const deletePage = (parent: ContentNode) => {
      for (const [idx, child] of parent.subContent.entries()) {
        // delete page and break recursion
        if (child.id === pageId) {
          parent.subContent.splice(idx, 1);
          return true;
        }
        // check children of current child
        if (deletePage(child)) {
          return true;
        }
      }
      return false;
    };

    const deleted = deletePage(updatedContent.contentData);
    if (!deleted) {
      localLog.error(`did not find page to delete with id ${pageId}`);
    }
    this.updateCurrentPage(updatedContent.contentData);
  };

  private handleValidationError = (validationError: any) => {
    if (validationError) {
      const { onValidationError } = this.props;
      if (onValidationError) {
        onValidationError(validationError);
      }
    }
  };

  // AUTO-SAVE debounce: schedules a working copy save 3 seconds after the last edit.
  // Prevents hammering localStorage/IndexedDB during typing. If called while already
  // queued, it's a no-op (the scheduled save will pick up the latest state).
  // Sets workingCopyStatus which drives a small "Saving..." / "Saved" indicator in the UI.
  private saveLocalWorkingCopy = () => {
    if (!this.savingLocalWorkingCopy) {
      this.setState({
        workingCopyStatus: WorkingCopyStatusType.Saving,
      });
      this.savingLocalWorkingCopy = setTimeout(async () => {
        try {
          const { contentId } = this.props;
          const { updatedContent, updatedContentAudience } = this.state;

          await this.workingCopyMngr.createContentWorkingCopy(
            contentId,
            this.workingCopyKey,
            ContentWorkingCopy.create(updatedContent, updatedContentAudience),
            true,
          );

          this.setState({
            workingCopyStatus: WorkingCopyStatusType.Saved,
          });
        } catch (e) {
          // Auto-save failed (e.g. storage quota exceeded) — show error indicator but
          // don't interrupt the user; they can still save via the Save button.
          this.setState({
            workingCopyStatus: WorkingCopyStatusType.Error,
          });
        } finally {
          delete this.savingLocalWorkingCopy;
        }
      }, 3000);
    }
  };

  private handleContentTypeMetadataInput = (
    sectionId: string,
    value: any,
    validationError?: string,
  ) => {
    const { updatedContent, metadataValidationError } = this.state;
    const { contentMetadata, contentTypeMetadata, templateMetadata } = updatedContent;
    const { templateId, templateVersion } = UtilApi.getCurrentTemplateInfo(value);
    // @ts-expect-error TS7053 - strict migration
    updatedContent.contentTypeMetadata[sectionId] = value;
    updatedContent.contentTypeMetadata.templateId = templateId;
    updatedContent.contentTypeMetadata.templateVersion = templateVersion;

    metadataValidationError.contentTypeMetadata[sectionId] = validationError;

    const templateMetadataSchema = UtilApi.getContentTemplateSchema(
      contentMetadata.contentType,
      contentTypeMetadata,
      ContentSchemaSection.TemplateMetadata,
    );

    let updatedTemplateMetadata = {};

    if (templateMetadataSchema && Array.isArray(templateMetadataSchema.required)) {
      updatedTemplateMetadata = Object.keys(templateMetadataSchema.properties).reduce(
        (acc, curKey) => {
          // @ts-expect-error TS7053 - strict migration
          acc[curKey] = templateMetadata[curKey];
          return acc;
        },
        {},
      );

      metadataValidationError.templateMetadata = {};
      // @ts-expect-error TS7006 - strict migration
      templateMetadataSchema.required.forEach((key) => {
        // @ts-expect-error TS7053 - strict migration
        if (!updatedTemplateMetadata[key]) {
          metadataValidationError.templateMetadata[key] = constants.REQUIRED;
        }
      });
      updatedContent.templateMetadata = updatedTemplateMetadata;
    }

    this.updateContent(updatedContent);

    this.setState({
      metadataValidationError,
    });
    this.handleValidationError(metadataValidationError);
  };

  private getAllowedContentAudienceOptions = () => {
    const { workflow } = this.props;
    // @ts-expect-error TS2339 - strict migration
    const { publicationWorkflows } = workflow;

    const contentAudienceRanking = [
      ContentAudience.INTERNAL,
      ContentAudience.EXTERNAL,
      ContentAudience.PUBLIC,
    ];
    // @ts-expect-error TS7006 - strict migration
    const contentAudienceHistory = publicationWorkflows.map((i) => i.contentAudience);

    const allowedContentAudienceOptions = UtilApi.deepClone(ContentAudienceOptions);
    // @ts-expect-error TS7006 - strict migration
    allowedContentAudienceOptions.forEach((item) => {
      // tslint:disable-next-line: max-line-length
      item.disabled = contentAudienceHistory.some(
        // @ts-expect-error TS7006 - strict migration
        (ca) =>
          contentAudienceRanking.indexOf(item.id as ContentAudience) <
          contentAudienceRanking.indexOf(ca),
      );
    });
    return allowedContentAudienceOptions;
  };

  private renderTitleSectionInEdit = () => {
    const { contentId } = this.props;
    const { updatedContent, fromContentType } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentName, contentShortName } = contentMetadata;
    return (
      <ContentTitle
        title={contentName}
        shortTitle={contentShortName}
        readonly={false}
        onTitleUpdated={this.handleContentMetadataInput}
        stretch
        duplicationCheckEnabled
        contentId={contentId}
      />
    );
  };

  private renderPatternPathslugSectionInEdit = () => {
    const { currentPage, updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentType } = contentMetadata;

    if (!currentPage) {
      return null;
    }

    const { id: pageId, href: pageHref, nodeType } = currentPage;
    const techwriterMetadata = { href: pageHref };
    const isGuideOrStrategy = guideOrStrategy(contentType);
    return isGuideOrStrategy ? null : (
      <TechwriterMetadataEditor
        readonly={false}
        visible={this.props.showTechwriterMetadata}
        metadata={techwriterMetadata}
        onInputUpdated={this.updateTechwriterMetadata}
        nodeType={nodeType}
        metadataId={pageId}
        key={pageId}
        contentType={contentType}
      />
    );
  };

  private updateTechwriterMetadata = (
    sectionKey: ContentSectionKey,
    value: any,
    validationError?: string,
  ) => {
    const { content } = this.props;
    const { currentPage } = this.state;
    currentPage[sectionKey] = value;
    const currentPageNode = findNodeById(currentPage.id, [content.contentData]);
    if (currentPageNode) {
      Object.assign(currentPageNode, { ...currentPage });
    }
    this.updateContent(content);
  };

  private renderDescriptionSectionInEdit = () => {
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const { description } = contentMetadata;

    const descriptionSection = (
      <div className="edit-content-description">
        <Container header={<Header variant="h3">{constants.DESCRIPTION_LABEL}</Header>}>
          <MetadataEditorInput
            sectionId="description"
            value={description}
            label={'Overview'}
            description={constants.CONTENT_DESCRIPTION_DESCRIPTION}
            onInputUpdated={this.handleContentMetadataInput}
            isRequired
            stretch
            minLength={UtilApi.descriptionInputMinLength}
            maxLength={UtilApi.descriptionInputMaxLength}
            removeFormatting
          />
          <InputSizeMessage
            input={description}
            maxNum={UtilApi.descriptionInputMaxLength}
            minNum={UtilApi.descriptionInputMinLength}
          />
        </Container>
      </div>
    );
    return descriptionSection;
  };

  // @ts-expect-error TS7030 - strict migration
  private renderContentConverter = () => {
    const { showContentConverter, toContentType, updatedContent } = this.state;
    if (showContentConverter) {
      return (
        <div>
          <ContentConverter
            content={updatedContent}
            onDismiss={this.dismissContentConverter}
            onSuccess={this.successContentConverter}
            // @ts-expect-error TS2322 - strict migration
            onError={this.errorContentConverter}
            // @ts-expect-error TS2322 - strict migration
            toContentType={toContentType}
          />
        </div>
      );
    }
  };

  private renderConfigurationSectionInEdit = () => {
    const { workflow, coAuthors, contentOwner, showTechwriterMetadata, publicationDate } =
      this.props;
    const { updatedContent, infoExpanded, toContentType, systemAddedTags } = this.state;
    const { contentMetadata, contentTypeMetadata, templateMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const metadataDivider = <hr id="metadata-divider" />;
    const lastDraftUpdateDate = null;

    const generalInfoSection = (
      <ColumnLayout columns={2}>
        <div>
          <MetadataSelectInput
            sectionId="contentType"
            label={constants.CONTENT_TYPE_LABEL}
            // @ts-expect-error TS2322 - strict migration
            selectedId={toContentType}
            selectOptions={ContentTypeOptions}
            // @ts-expect-error TS2322 - strict migration
            onSelectUpdated={this.handleContentTypeInput}
            readonly={false}
          />
          {this.renderContentConverter()}
        </div>
        <div>
          {/* @ts-expect-error TS2322 - strict migration */}
          <ContentVersion workflowId={workflow && workflow.draftWorkflowId} />
          {lastDraftUpdateDate}
          <MetadataDisplay
            label={constants.AUTHORS_METADATA_LABEL}
            // @ts-expect-error TS2345 - strict migration
            value={UtilApi.displayByline(contentOwner, coAuthors, workflow)}
          />
          <LastPublicationDate workflow={workflow} publicationDate={publicationDate} />
        </div>
      </ColumnLayout>
    );

    const otherMetadataSection = (
      <Grid gridDefinition={[{ colspan: 11 }]}>
        <ContentMetadataForm
          autotags={systemAddedTags}
          contentType={contentType}
          contentTypeMetadata={contentTypeMetadata}
          onContentMetadataUpdated={this.handleContentMetadataInput}
          onContentTypeMetadataUpdated={this.handleContentTypeMetadataInput}
          onTemplateMetadataUpdated={this.handleTemplateMetadataInput}
          readonly={false}
          // tslint:disable-next-line: max-line-length
          templateSchema={UtilApi.getContentTemplateSchema(
            contentType,
            contentTypeMetadata,
            ContentSchemaSection.TemplateMetadata,
          )}
          templateData={templateMetadata}
          visible
        />
      </Grid>
    );

    return (
      <ExpandableSection
        header={<Header variant="h3">{constants.CONFIGURATION_SECTION_HEADER}</Header>}
        variant="container"
        expanded={infoExpanded}
        onChange={this.toggleInfoExpanded}
        id="edit-content-configuration"
      >
        <div id="metadata">
          {generalInfoSection}
          {metadataDivider}
          {otherMetadataSection}
        </div>
      </ExpandableSection>
    );
  };

  private renderAudienceSectionInEdit = () => {
    const { publicationDate } = this.props;
    const { updatedContentAudience } = this.state;
    const allowedContentAudienceOptions = this.getAllowedContentAudienceOptions();
    const isEdittingContentAudienceDisabled = !!publicationDate;
    return (
      <Container
        header={
          <Header variant="h3" info={<InfoLink topic={'intended-audience'} />}>
            {constants.INTENDED_AUDIENCE_SECTION_HEADER}
          </Header>
        }
      >
        <MetadataTileInput
          sectionId="contentAudience"
          selectedTile={updatedContentAudience}
          tileOptions={allowedContentAudienceOptions}
          label={constants.CONTENT_AUDIENCE_LABEL}
          description={constants.INTENDED_AUDIENCE_SECTION_DESCRIPTION}
          placeholder={constants.CONTENT_AUDIENCE_PLACEHOLDER}
          onInputUpdated={this.handleContentAudienceChanged}
          readonly={isEdittingContentAudienceDisabled}
          isRequired
          stretch
          visible
        />
        <NoticeOfPublication
          contentAudience={updatedContentAudience}
          visible={this.isEditingEnabled}
          text={
            // @ts-expect-error TS7006 - strict migration
            allowedContentAudienceOptions.some((i) => i.disabled)
              ? constants.CONTENT_AUDIENCE_UPGRADE_NOTICE
              : ''
          }
        />
      </Container>
    );
  };

  private renderContentTab = () => {
    const { expanded } = this.state;
    const editContentControls = (
      <Box variant="div" margin={{ right: 's' }}>
        <div id="edit-content-controls">
          <EditContentControls {...this.getEditContentControlProps()} />
        </div>
      </Box>
    );
    const { inlineComments, showInlineCommentsInEditMode } = this.props;
    const openComments = inlineComments?.filter(
      (comment) => comment.state === UserCommentState.PENDING,
    );
    const showInlineCommentPrefix = showInlineCommentsInEditMode ? 'Hide' : 'Review';
    const inlineCommentsToReview = openComments?.length ? (
      <Box padding={{ left: 's' }} display="inline">
        <Button iconName="contact" onClick={this.handleReviewComment}>
          {`${showInlineCommentPrefix} ${openComments.length} comment${openComments.length > 1 ? 's' : ''}`}
        </Button>
      </Box>
    ) : (
      <Box display="inline">
        <Box display="inline-block" padding={{ left: 's', right: 'xxs' }}>
          <Icon name="contact" />
        </Box>
        No comments to review
      </Box>
    );

    const collapseExpand = (
      <Box display="inline">
        <Button
          variant="icon"
          iconName={expanded ? 'treeview-collapse' : 'treeview-expand'}
          ariaLabel={expanded ? 'Collapse all sections' : 'Expand all sections'}
          onClick={this.toggleExpanded}
        />
        {expanded ? 'Collapse all' : 'Expand all'}
      </Box>
    );
    return (
      <SpaceBetween size="m">
        <Box float="right">
          {collapseExpand}
          {inlineCommentsToReview}
        </Box>
        {this.renderContentSection()}
        {editContentControls}
      </SpaceBetween>
    );
  };

  private renderCodeTab = () => {
    const { peerReviewers } = this.props;
    const { updatedContent, updatedContentAudience } = this.state;
    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;
    const { contentOwner, workflow, coAuthors } = this.props;
    const allowedToSeeAppSecLink = isAllowedToSeeAppSecLink(
      userProfile,
      contentOwner,
      workflow,
      coAuthors,
      // @ts-expect-error TS2345 - strict migration
      peerReviewers,
    );
    const allowedRepoType = contentAudienceToRepoTypeMap.get(updatedContentAudience);

    return (
      <CodeRepoList
        userType={userProfile?.type}
        isAllowedToSeeAppSecLink={allowedToSeeAppSecLink}
        codeLinks={updatedContent.contentTypeMetadata.codeLinks}
        handleAddLink={this.addOrReplaceCodeRepo}
        allowedRepoType={allowedRepoType}
      />
    );
  };

  private renderAuthorsTab = () => {
    const { coAuthors, contentOwner, peerReviewers } = this.props;
    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;

    return (
      <AuthorsList
        // @ts-expect-error TS2322 - strict migration
        owner={contentOwner}
        coAuthors={coAuthors}
        currentUser={userProfile}
        peerReviewers={peerReviewers}
        contentType={this.state.updatedContent.contentMetadata.contentType}
        {...this.props}
      />
    );
  };

  private renderAttachmentListInEdit = () => {
    const { contentId } = this.props;
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    return (
      <AttachmentList
        // @ts-expect-error TS2783 - strict migration
        contentId={contentId}
        // @ts-expect-error TS2322 - strict migration
        attachmentIds={updatedContent.attachments}
        readOnly={!this.isEditingEnabled}
        onUpdate={this.updateAttachments}
        onDelete={this.removeAttachment}
        tabView
        contentType={contentType}
        {...this.props}
      />
    );
  };

  private renderReviewersTab = () => {
    const { showTechwriterMetadata, isAdminView } = this.props;
    const { isContentAuthorCoAuthor } = this.state;

    const submitForReviewControl =
      (!isContentAuthorCoAuthor || isAdminView) && !showTechwriterMetadata ? (
        <Box float="right">
          <SubmitForReviewControl {...this.getSubmitForReviewControlProps()} />
        </Box>
      ) : null;

    return (
      <>
        <AuthorControls {...this.getAuthorControlsProps()} />
        <SpaceBetween direction={'vertical'} size="xxxs">
          {submitForReviewControl}
        </SpaceBetween>
      </>
    );
  };

  private onUpdateReviewers = (updatedReviewers: ContentAccessUser[]) => {
    this.setState({ reviewers: updatedReviewers });
  };

  private renderHistoryTab = () => {
    const { contentId, workflowHistory, workflowId } = this.props;
    return (
      <PublicationHistory
        contentId={contentId}
        workflowId={workflowId}
        visible
        // @ts-expect-error TS2322 - strict migration
        workflows={workflowHistory}
      />
    );
  };

  private renderCodeRepoValidationError = () => {
    const { showCodeValidationError } = this.props;
    const { codeRepoValidationError } = this.state;
    return showCodeValidationError && isCodeInvalid(codeRepoValidationError) ? (
      <Alert statusIconAriaLabel="Error" type="error" header="We found issues with your content">
        {constants.CODE_VALIDATION_ERROR_INTRO_TEXT}
        <TextContent>
          <ul>
            {codeRepoValidationError.map((error) =>
              !!error ? <li key={error}>{error}</li> : null,
            )}
          </ul>
        </TextContent>
      </Alert>
    ) : null;
  };

  private renderTabs = () => {
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const detailsTab = (
      <SpaceBetween size="xl">
        {this.renderDescriptionSectionInEdit()}
        {this.renderConfigurationSectionInEdit()}
        {this.renderAudienceSectionInEdit()}
      </SpaceBetween>
    );
    const contentTab = this.renderContentTab();
    const codeTab = this.renderCodeTab();
    const attachmentTab = this.renderAttachmentListInEdit();
    const authorsTab = this.renderAuthorsTab();
    const reviewersTab = this.renderReviewersTab();
    const historyTab = this.renderHistoryTab();

    const allTabs = [
      {
        content: detailsTab,
        id: 'details',
        label: constants.DETAILS_TAB_LABEL,
      },
      {
        content: contentTab,
        id: 'content',
        label: constants.CONTENT_TAB_LABEL,
      },
      {
        content: codeTab,
        disabled:
          !supportsCodeRepos(contentType) ||
          // @ts-expect-error TS2532 - strict migration
          this.props.appContext.authnInfo.userProfile.type !== 'AWS',
        id: 'code',
        label: 'Code',
      },
      {
        content: attachmentTab,
        id: 'attachments',
        label: constants.ATTACHMENTS_TAB_LABEL,
      },
      {
        content: authorsTab,
        id: 'authors',
        label: 'Authors',
      },
      {
        content: reviewersTab,
        id: 'reviewers',
        label: constants.REVIEWER_TAB_LABEL,
      },
      {
        content: historyTab,
        id: 'history',
        label: constants.HISTORY_TAB_LABEL,
      },
    ];

    return (
      <Tabs
        activeTabId={this.state.activeTabId}
        ariaLabel="Content editing navigation"
        tabs={allTabs}
        // @ts-expect-error TS2322 - strict migration
        onChange={this.handleTabChange}
      />
    );
  };

  private renderEditContentTabView = () => {
    const { workflow, showTechwriterMetadata, isAdminView } = this.props;
    const { isContentAuthorCoAuthor, updatedContent, reviewers } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const pageHeaderTitle = !!showTechwriterMetadata
      ? finalizeContentPageHeaders.get(contentType)
      : editContentPageHeaders.get(contentType);

    const pageHeader = (
      <>
        <Box display="inline" variant="h2">
          {pageHeaderTitle}
        </Box>
        <Box display="inline" padding={{ left: 'xs' }}>
          <TutorialLink
            // @ts-expect-error TS2322 - strict migration
            contentType={workflow?.draftContentMetadata?.contentType}
            schemaSection={ContentSchemaSection.ContentTypeMetadata}
          />
        </Box>
        <p>{createEditContentPageInstructions.get(contentType)}</p>
      </>
    );

    // Do not show NoReviewerAlert at the bottom when
    // 1. Is in finalizing state or user is author/co-author of the content, and
    // 2. Reviewer is selected
    const bottomNoReviewerAlert =
      isContentAuthorCoAuthor ||
      !!showTechwriterMetadata ||
      this.validateReviewers(reviewers) ? null : (
        <Box margin={{ top: 'm' }}>{noReviewerAlert}</Box>
      );

    // 1. Not in finalizing view, and
    // 2. User is the author or co-author of the content, and
    // 3. Is not in admin view
    // 4. Not in peer review stage
    const contentChecklist =
      !!showTechwriterMetadata ||
      !isContentAuthorCoAuthor ||
      !!isAdminView ||
      isInPeerReview(workflow) ? null : (
        // @ts-expect-error TS2322 - strict migration
        <ContentChecklist {...this.getContentChecklistProps()} />
      );

    const evaluationCard = <ContentQualityEvalucationCard contentId={this.props.contentId} />;

    return (
      <div id="edit-content">
        {pageHeader}
        <Grid
          gridDefinition={[
            { colspan: { xs: 12, s: 9, m: 10 } },
            { colspan: { xs: 6, s: 3, m: 2 } },
          ]}
        >
          <Box margin={{ top: 's' }}>
            <Form>
              {this.renderValidationFormError()}
              {this.renderTitleSectionInEdit()}
              {this.renderPatternPathslugSectionInEdit()}
              {this.renderCodeRepoValidationError()}
              {this.renderTabs()}
            </Form>
            {bottomNoReviewerAlert}
          </Box>
          <div id="right-controls-container">
            {this.renderEditRightColumn(contentChecklist, evaluationCard)}
          </div>
        </Grid>
      </div>
    );
  };

  private renderEditRightColumn = (
    contentChecklist: React.ReactNode,
    evaluationCard: React.ReactNode,
  ) => {
    const { showInlineCommentsInEditMode, setShowInlineCommentsInEditMode, contentId, workflowId } =
      this.props;
    if (!showInlineCommentsInEditMode) {
      return (
        <>
          {this.renderControls()}
          {contentChecklist}
          {evaluationCard}
        </>
      );
    }
    return (
      <InlineCommentList
        setShowAllComment={setShowInlineCommentsInEditMode}
        contentId={contentId}
        workflowId={workflowId}
      />
    );
  };

  private handleAddReviewer = () => {
    window.scrollTo({ behavior: 'smooth', top: 0 });
    this.setState({
      activeTabId: 'reviewers',
    });
  };

  private handleReviewComment = () => {
    const { setShowInlineCommentsInEditMode } = this.props;
    const { showInlineCommentsInEditMode } = this.props;
    if (setShowInlineCommentsInEditMode) {
      setShowInlineCommentsInEditMode(!showInlineCommentsInEditMode);
    }
  };

  private renderValidationFormError = () => {
    const { formError } = this.state;
    return (
      <Alert
        type="error"
        visible={!!formError}
        dismissible
        onDismiss={() => this.setState({ formError: null })}
        header={constants.VALIDATION_SUMMARY_HEADER}
      >
        {formError}
      </Alert>
    );
  };

  // Combined "can submit for review?" check — used to disable the Submit button.
  // Content is submittable only when ALL are true:
  //   1. All required fields filled in (contentValid)
  //   2. Code validation passes (when code-audience checking is enabled)
  //   3. No parent-reported validation error
  //   4. Content has been changed since last submit (or hasn't been submitted yet)
  //   5. Reviewers are set (partner-owned content requires an AWS reviewer)
  private isSubmitForReviewDisabled = () => {
    const { validationError, showCodeValidationError } = this.props;
    const {
      isContentUnchanged,
      isContentNewOrModified,
      metadataValidationError,
      contentDataValidationError,
      reviewers,
      codeRepoValidationError,
    } = this.state;
    const contentValid = isContentValid(metadataValidationError, contentDataValidationError);
    const codeInvalid = showCodeValidationError && isCodeInvalid(codeRepoValidationError);

    return (
      !contentValid ||
      codeInvalid ||
      !!validationError ||
      (!isContentNewOrModified && isContentUnchanged) ||
      !this.validateReviewers(reviewers)
    );
  };

  // "Pre-flight" validation for the actual Submit-for-Review action. Combines beforeSaving()
  // (which validates AND surfaces errors in the form) with reviewer validation. If anything
  // fails, scrolls to the top of the page so the user sees the error summary.
  private validateSubmitForReview = async () => {
    const { validationError } = this.props;
    const { reviewers } = this.state;

    const isContentValid = await this.beforeSaving();
    const isValid = !validationError && isContentValid && this.validateReviewers(reviewers);

    if (!isValid) {
      // scroll to top to see error messages
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }

    return isValid;
  };

  private handleResetChecklist = () => {
    this.setState({
      shouldResetChecklist: false,
    });
  };

  private getContentChecklistProps = () => {
    const { workflow } = this.props;
    const {
      isContentUnchanged,
      reviewers,
      updatedContent,
      updatedContentAudience,
      shouldResetChecklist,
    } = this.state;

    return {
      handleAddReviewer: this.handleAddReviewer,
      handleChecklistComplete: this.handleChecklistComplete,
      handleChecklistIncomplete: this.handleChecklistIncomplete,
      handleSaveChanges: this.saveChanges,
      handleSubmitForReview: this.exitEditModeAndSubmitForReview,
      isContentUnchanged,
      isReviewersValid: this.validateReviewers(reviewers),
      submitForReviewDisabled: this.isSubmitForReviewDisabled(),
      validateSubmitForReview: this.validateSubmitForReview,
      workflow,
      updatedContent,
      updatedContentAudience,
      shouldResetChecklist,
      handleResetChecklist: this.handleResetChecklist,
    };
  };

  private getAuthorControlsProps = () => {
    const { contentId, workflow, validationError, isAdminView } = this.props;
    const {
      updatedContent,
      isLoading,
      isEditReviewersEnabled,
      isDeleteReviewersEnabled,
      isContentChecklistComplete,
      isContentUnchanged,
    } = this.state;
    const disableSubmitForReview = this.isSubmitForReviewDisabled() || !isContentChecklistComplete;
    // @ts-expect-error TS2339 - strict migration
    const { lastReviewDecision, lastReviewRequestDecision } = workflow;
    const showCannotDeleteReviewerAlert =
      isContentChecklistComplete &&
      lastReviewDecision === ReviewDecision.DISAPPROVE &&
      lastReviewRequestDecision !== ReviewRequestDecision.DECLINE &&
      !isAdminView;
    return {
      contentId,
      content: updatedContent,
      workflow,
      disabled: false,
      validationError,
      onActionSuccess: null,
      onUpdateReviewers: this.onUpdateReviewers,
      loading: isLoading,
      isEditReviewersDisabled: !isEditReviewersEnabled,
      isDeleteReviewersDisabled: !isDeleteReviewersEnabled,
      showCannotDeleteReviewerAlert,
      isSubmitForReviewDisabled: disableSubmitForReview,
      isContentUnchanged,
      handleSaveChanges: this.saveChanges,
      handleSubmitForReview: this.exitEditModeAndSubmitForReview,
      validateSubmitForReview: this.validateSubmitForReview,
    };
  };

  private getEditContentControlProps = () => {
    const { showTechwriterMetadata, workflow } = this.props;
    const {
      isContentAuthorCoAuthor,
      isContentUnchanged,
      metadataValidationError,
      contentDataValidationError,
    } = this.state;
    const contentValid = isContentValid(metadataValidationError, contentDataValidationError);
    return {
      isContentUnchanged,
      isValid: contentValid,
      onBeforeSaving: this.beforeSaving,
      onDiscardChanges: this.discardChanges,
      onSaveChanges: this.saveChanges,
      onSubmitForReview: this.exitEditModeAndSubmitForReview,
      showSubmitForReviewButton: !isContentAuthorCoAuthor && !showTechwriterMetadata,
      submitForReviewDisabled: this.isSubmitForReviewDisabled(),
      validateSubmitForReview: this.validateSubmitForReview,
      isInPeerReview: isInPeerReview(workflow),
    };
  };

  private getSubmitForReviewControlProps = () => {
    const { isContentUnchanged } = this.state;
    return {
      disabled: this.isSubmitForReviewDisabled(),
      isContentUnchanged,
      onSaveChanges: this.saveChanges,
      onSubmitForReview: this.exitEditModeAndSubmitForReview,
      validateSubmitForReview: this.validateSubmitForReview,
      workflow: this.props.workflow,
    };
  };

  /*    private handleTabChange = (event: CustomEvent<TabsProps.ChangeDetail>) => {
        const { activeTabId } = event.detail;
        this.setState({ activeTabId });
    } */

  private loadReviewers = async () => {
    this.setState({
      isLoading: true,
    });
    const { contentId } = this.props;
    const { accessUsers } = await this.mplService.listContentReviewers(contentId);

    this.setState({
      isLoading: false,
      reviewers: accessUsers,
    });
  };

  // WORKING COPY RESTORATION on mount.
  // Checks localStorage/IndexedDB for a previously auto-saved working copy and restores
  // it if newer than the backend's last-modified timestamp. This recovers unsaved edits
  // after a browser crash, accidental tab close, or page refresh.
  //
  // Logic:
  //   1. Fetch most recent working copy for this content + workingCopyKey
  //   2. If it has updated inline comments, push them to the parent (they'll be shown pending)
  //   3. If no working copy exists OR it's older than the backend, skip restoration
  //   4. Otherwise restore the working copy's content + audience into state and notify parent
  //   5. Flash an info message so user knows why they see unsaved changes
  private loadWorkingCopy = async () => {
    try {
      this.setState({
        isLoadingWorkingCopy: true,
      });

      const mostRecentWorkingCopy = await this.workingCopyMngr.getMostRecentWorkingCopy(
        this.props.contentId,
        this.workingCopyKey,
      );
      const updatedInlineComments = mostRecentWorkingCopy?.workingCopy?.updatedInlineComments;
      if (updatedInlineComments?.length) {
        // @ts-expect-error TS2722 - strict migration
        this.props.setInlineCommentsData(updatedInlineComments);
      }
      if (
        !mostRecentWorkingCopy ||
        mostRecentWorkingCopy.createdAt < this.props.contentLastModified
      ) {
        // No working copy OR backend is newer (someone else saved while we were away)
        // → discard the local copy and use backend content.
        return this.setState({
          isLoadingWorkingCopy: false,
        });
      }

      this.setState({
        isContentUnchanged: false,
        workingCopyStatus: WorkingCopyStatusType.Saved,
        updatedContent: mostRecentWorkingCopy.workingCopy.content,
        // @ts-expect-error TS2322 - strict migration
        updatedContentAudience: mostRecentWorkingCopy.workingCopy.contentAudience,
      });

      await this.props?.onContentDataChanged(mostRecentWorkingCopy.workingCopy.content);
      // @ts-expect-error TS2345 - strict migration
      await this.props?.onContentAudienceChanged(mostRecentWorkingCopy.workingCopy.contentAudience);

      this.flashbarMessageBroker.pushInfoMessage(constants.WORKFLOW_WORKING_COPY_RESTORED_INFO);
    } finally {
      this.setState({
        isLoadingWorkingCopy: false,
      });
    }
  };

  private addSectionsFromTemplate = (
    updatedContent: Content,
    contentTemplate: Content,
    sectionsToAdd?: ContentNode[],
  ): boolean => {
    const { contentSections: templateSections } = contentTemplate.contentData;
    const { contentSections } = updatedContent.contentData;
    const contentSectionIds = contentSections.map((contentSection) => contentSection.id);
    const sectionIdsToAdd = sectionsToAdd?.map((sectionToAdd) => sectionToAdd.id);
    let sectionsAdded = false;
    templateSections.forEach((templateSection: ContentNode, index: number) => {
      if (
        !contentSectionIds.includes(templateSection.id) &&
        (!sectionIdsToAdd || sectionIdsToAdd.includes(templateSection.id))
      ) {
        const newSection = { ...templateSection };
        // @ts-expect-error TS2322 - strict migration
        newSection.href = getNodeHref(newSection, updatedContent.contentMetadata);
        contentSections.splice(index, 0, newSection);
        sectionsAdded = true;
      }
    });
    return sectionsAdded;
  };

  // LEGACY DATA FIX: adds missing required sections to Pattern content.
  // Older Pattern content may have been created when fewer sections were required.
  // This patches them in from the current template so the editor doesn't show empty spots
  // for fields the schema now requires. Only runs for Patterns because other types have
  // different section structures.
  private addNewContentSections = async () => {
    const { updatedContent } = this.state;
    const { contentType } = updatedContent.contentMetadata;
    const { templateId } = updatedContent.contentTypeMetadata;
    try {
      this.setState({ isLoadingContentTemplate: true });
      if (
        ContentType.Pattern === contentType &&
        missingRequiredSections(updatedContent).length > 0
      ) {
        // Get template from service so that section content data can be initialized
        const contentTemplateResponse = await this.mplService.getContentTemplate(templateId);
        const sectionsAdded = this.addSectionsFromTemplate(
          updatedContent,
          contentTemplateResponse.content,
        );
        if (sectionsAdded) {
          this.setState({ isContentUnchanged: false, updatedContent });
        }
      }
    } finally {
      this.setState({ isLoadingContentTemplate: false });
    }
  };

  private handleChecklistComplete = () => {
    this.setState({ isContentChecklistComplete: true });
  };

  private handleChecklistIncomplete = () => {
    this.setState({ isContentChecklistComplete: false });
  };

  // CONTENT-TYPE CONVERSION flow (Pattern ↔ Guide ↔ Strategy).
  // Entry point: user picks a different type in the Details tab's type selector.
  //   - If no change → nothing to do.
  //   - If already converted once this session → block (avoid data loss from double-conversion)
  //     and revert the selector.
  //   - Otherwise → open the ContentConverter wizard which handles the section re-mapping.
  private handleContentTypeInput = (sectionId: string, toContentType: ContentType) => {
    const { contentConverted, updatedContent } = this.state;
    const { contentType } = updatedContent.contentMetadata;
    if (contentType !== toContentType) {
      if (contentConverted) {
        this.setState({
          toContentType: contentType,
        });
        this.flashbarMessageBroker.pushErrorMessage(
          constants.CONTENT_CONVERSION_ALREADY_CONVERTED_ERROR,
        );
      } else {
        this.setState({
          showContentConverter: true,
          toContentType,
        });
      }
    }
  };

  // Called by the conversion wizard on success. Receives the converted content plus
  // any CommentConversions (inline comment re-anchoring from old sections to new sections).
  // Converting TO Pattern typically requires filling in extra required fields, so we
  // land the user on Details with a warning. Converting FROM Pattern usually lands on
  // Content with a success flashbar.
  private successContentConverter = (
    updatedContent: Content,
    commentConversions?: CommentConversion[],
  ) => {
    const { fromContentType, toContentType } = this.state;
    // @ts-expect-error TS18048 - strict migration
    const successMsg = `${fromContentType} successfully converted to a ${toContentType.toLocaleLowerCase()}`;
    // @ts-expect-error TS18048 - strict migration
    const warnMsg = `Please fill in required fields to complete conversion from ${fromContentType.toLocaleLowerCase()} to ${toContentType.toLocaleLowerCase()}`;
    const activeTabId = toContentType === ContentType.Pattern ? 'details' : 'content';

    if (toContentType === ContentType.Pattern) {
      this.flashbarMessageBroker.pushWarnMessage(warnMsg);
    } else {
      this.flashbarMessageBroker.pushSuccessMessage(successMsg);
    }
    const shouldResetChecklist = shouldResetContentChecklistDueToContentTypeChange(
      // @ts-expect-error TS2345 - strict migration
      fromContentType,
      toContentType,
    );
    this.setState({
      activeTabId,
      contentConverted: true,
      currentPage: updatedContent.contentData,
      isContentUnchanged: false,
      commentConversions,
      showContentConverter: false,
      updatedContent,
      shouldResetChecklist,
    });
  };

  private dismissContentConverter = () => {
    const { fromContentType } = this.state;
    this.setState({
      showContentConverter: false,
      toContentType: fromContentType,
    });
  };

  private errorContentConverter = (errorMsg: string) => {
    this.dismissContentConverter();
    this.flashbarMessageBroker.pushErrorMessage(constants.CONTENT_CONVERSION_ERROR);
    localLog?.error(`Content conversion error ${errorMsg}`);
  };

  private validateReviewers = (reviewers: ContentAccessUser[] = []) => {
    const { contentOwner } = this.props;
    return isReviewersValid(reviewers, contentOwner?.type === UserType.Partner);
  };
}

export default EditContentData;
